/**
 * V-neck blouse designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_blouse_designs`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const vNeckBlouseDesignsPack: ToilePack = {
    slug: 'v-neck-blouse-designs',
    category: 'fashion',
    name: 'V-neck blouse designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'deep', label: 'Deep V', subject: 'a blouse with a deep V front neck and elbow sleeves'},
        {key: 'piping', label: 'V with piping', subject: 'a V-neck blouse with contrast piping and a small brooch'},
        {key: 'back', label: 'V back', subject: 'the back of a blouse with a V cut and a dori'},
    ],
    styles: [
        {key: 'tailor', label: "Tailor's shop", style: "photographed in an Indian tailor's shop with bolts of fabric and a measuring tape soft and out of focus behind, warm daylight from a window camera-left, gentle shadow on the far side, a balance of two-thirds light to one-third shade", palette: 'royal blue silk with gold', framing: 'The dress form centered from waist to neck with room around it'},
        {key: 'boutique', label: 'Boutique', style: 'photographed in a bright boutique against a plain pale plaster wall, soft even daylight near-frontal, a faint shadow on the wall, an airy balance of mostly light with little shade', palette: 'peach with a cream border', framing: 'The dress form centered from waist to neck with a clear margin of wall on each side'},
        {key: 'closeup', label: 'Close-up', style: 'a close view in daylight raking from camera-right, every thread, bead and seam crisp, a balance of deep shade in the folds and bright highlights on the work', palette: 'bottle green with gold', framing: 'The blouse filling the frame, the dress form cropped at the waist'},
    ],
    directions: ['silk saree', 'full sleeves', 'for party'],
    directionHint: 'try: silk saree',
    seo: {
        title: 'V-Neck Blouse Designs: Deep V, Piped, V Back',
        metaDescription: 'Nine V-neck blouses: a deep V, a V with piping and a V back, each in three views.',
        h1: 'V-neck blouse designs: deep V, piped V and V back',
        tagline: "Three V cuts, each in a tailor's shop, a bright boutique, and as a close-up of the neckline.",
        intro: "V-neck saree blouses in three cuts: a deep V front with elbow sleeves, a V neckline with contrast piping and a small brooch, and a back with a V cut and a dori. Each sits on a dress form in a tailor's shop, a bright boutique or as a close-up of the neckline. Add a direction, such as 'silk saree' or 'for party', to ask for a change; the picture is a request and may differ.",
        useCases: [
            'Showing a tailor the depth of the V',
            'A piped V neck for an office blouse',
            'A V back for a party blouse',
            'Comparing a plain V with a piped one',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'V cuts in three readings: a deep V front, a V with contrast piping, and a V back with a dori.'},
            {q: 'What are the three views?', a: "A tailor's shop, a bright boutique and a close-up of the neckline."},
            {q: 'Can I change the fabric or the occasion?', a: "Yes. Add a direction such as 'silk saree' or 'for party'. It is a request, so the result may not match the nine."},
            {q: 'Can I print these?', a: 'They are portrait-shaped pictures, so they suit a portrait page. Check the size you need before you print.'},
        ],
    },
}
